import { useEffect, useRef } from "react";

interface PricingProps {
  onRequestLeads: () => void;
}

const perks = [
  "No monthly contracts",
  "Pay per lead — $100 to $200",
  "5-minute reservation system",
  "Full dashboard access included",
];

export default function Pricing({ onRequestLeads }: PricingProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const card = entry.target.querySelector(".pricing-card") as HTMLElement;
            if (card) {
              card.style.opacity = "1";
              card.style.transform = "translateY(0)";
            }
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="pricing" className="py-24 md:py-32 bg-gray-50" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-green-600 text-xs font-bold uppercase tracking-widest mb-3 block">Pricing</span>
          <h2 className="text-3xl md:text-5xl font-bold text-black tracking-tight">
            Simple, Performance-Based Pricing
          </h2>
          <p className="mt-4 text-gray-500 text-lg max-w-xl mx-auto">
            Pay only for leads that match your criteria. No retainers, no surprises.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div
            className="pricing-card bg-white rounded-3xl p-8 md:p-12 border border-gray-100"
            style={{ opacity: 0, transform: "translateY(32px)", transition: "opacity 0.6s ease, transform 0.6s ease" }}
          >
            {/* Price */}
            <div className="flex flex-col sm:flex-row sm:items-end gap-2 mb-2">
              <span className="text-5xl md:text-7xl font-black text-black leading-none">$100–$200</span>
              <span className="text-gray-400 text-lg mb-1">per exclusive lead</span>
            </div>
            <p className="text-green-600 text-sm font-semibold mb-8">Intro pricing — locked in for early partners</p>

            {/* Perks */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {perks.map((perk) => (
                <div key={perk} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-green-500 text-lg"></i>
                  </div>
                  <span className="text-gray-700 text-sm font-medium">{perk}</span>
                </div>
              ))}
            </div>

            {/* Highlight box */}
            <div className="bg-green-50 border border-green-100 rounded-2xl p-5 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-dashboard-line text-green-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-green-800 font-bold text-base">Full dashboard included</p>
                  <p className="text-green-700 text-sm mt-1">
                    Browse the live marketplace, filter by score and price, track leads through your pipeline, and manage billing — all in one place.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={onRequestLeads}
              className="w-full bg-black hover:bg-gray-900 text-white font-bold py-4 rounded-2xl text-base transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              Get Test Leads
              <i className="ri-arrow-right-line text-lg"></i>
            </button>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-6 mt-6 pt-6 border-t border-gray-100">
              {[
                { icon: "ri-lock-line", label: "No Contracts" },
                { icon: "ri-store-2-line", label: "Live Marketplace" },
                { icon: "ri-shopping-bag-line", label: "Pipeline Tracking" },
              ].map((badge) => (
                <div key={badge.label} className="flex items-center gap-1.5 text-gray-400 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${badge.icon} text-sm`}></i>
                  </div>
                  <span>{badge.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
